import { type Theme, useTheme } from '@mui/material'
import { ComputedNode, ResponsiveTreeMapHtml } from '@nivo/treemap'
import { Colors, EmptyState, formatNumberWithShortAbbreviation, MonetaryValue, MonetaryValueDataObjectType, tokens } from 'ComponentLibrary'
import TreeMapPortfolioTooltip from './TreeMapPortfolioTooltip'
import { ComponentProps } from 'react'
import { TreeMapHtmlNode } from './TreeMapNode'
import { Headline } from 'ComponentLibrary/src/piazza/text/Headline/Headline'
import { useNavigate } from 'react-router-dom'
import { type TreeMapPortfolioDatum } from './types'
import { withNivoTooltipWrapperTheme } from 'ComponentLibrary/src/common/nivoTheme'

export type PortfolioData = {
    name: string
    children: {
        labelStreetAndHouseNumber: string
        value: MonetaryValueDataObjectType
        labelCityAndReference: string
        trend?: number
        href?: string
        percentage: number
    }[]
}
export type TreeMapPortfolioTranslations = {
    title?: string
    tooltipMarketValueLabel?: string
    tooltipValueTrendLabel?: string
    tooltipPortfolioShareLabel?: string
}

export type TreeMapPortfolioProps = {
    portfolioData: PortfolioData[]
    emptyStateTitle?: string
    translations?: TreeMapPortfolioTranslations
    formatValueLabel?: (value: number) => string
    formatTooltipValue?: (value: number) => string
    formatTooltipPercentage?: (value: number) => string
}

const DEFAULT_EMPTY_STATE_TITLE = 'Es stehen noch nicht genügend Daten zur Verfügung'
const DEFAULT_TITLE = 'Marktwertanteil'
const DEFAULT_TOOLTIP_MARKET_VALUE_LABEL = 'Marktwert'
const DEFAULT_TOOLTIP_VALUE_TREND_LABEL = 'Wertentwicklung'
const DEFAULT_TOOLTIP_PORTFOLIO_SHARE_LABEL = 'Marktwertanteil Portfolio'

export const TreeMapPortfolio = ({
    portfolioData,
    emptyStateTitle = DEFAULT_EMPTY_STATE_TITLE,
    translations,
    formatValueLabel = (value) => `${formatNumberWithShortAbbreviation(value)} €`,
    formatTooltipValue,
    formatTooltipPercentage,
}: TreeMapPortfolioProps) => {
    // Create data which consists of portfolios with assets as children. Each assets have a value and a change value. If the change value is negative the color is red, otherwise green.
    const theme = useTheme()
    const title = translations?.title ?? DEFAULT_TITLE
    const tooltipMarketValueLabel = translations?.tooltipMarketValueLabel ?? DEFAULT_TOOLTIP_MARKET_VALUE_LABEL
    const tooltipValueTrendLabel = translations?.tooltipValueTrendLabel ?? DEFAULT_TOOLTIP_VALUE_TREND_LABEL
    const tooltipPortfolioShareLabel = translations?.tooltipPortfolioShareLabel ?? DEFAULT_TOOLTIP_PORTFOLIO_SHARE_LABEL
    const data = portfolioData.map((portfolio) => {
        const children: TreeMapPortfolioDatum[] = portfolio.children.map(
            ({ value, trend, labelStreetAndHouseNumber, href, percentage, labelCityAndReference }) => {
                // undefined and trend === 0 are displayed as green

                const color = getTrendColor(trend)
                return {
                    value: MonetaryValue.fromMonetaryValueDataObject(value).toDecimal(),
                    labelStreetAndHouseNumber,
                    labelCityAndReference,
                    trend,
                    color,
                    href,
                    percentage,
                }
            }
        )
        return {
            ...portfolio,
            color: theme.palette.secondary1.grey,
            children,
        }
    })

    const showEmptyState = data.length === 0 || data.every((portfolio) => portfolio.children.length === 0)
    const navigate = useNavigate()

    return (
        <>
            <Headline variant="headline3" title={title} />
            {!showEmptyState ? (
                <ResponsiveTreeMapHtml<TreeMapPortfolioDatum>
                    data={{ color: 'transparent', children: data }}
                    identity="name"
                    tile="squarify"
                    value="value"
                    theme={customTheme(theme)}
                    margin={{ top: -24, right: -10, bottom: 16, left: -10 }}
                    labelSkipSize={35}
                    label={({ value }) => formatValueLabel(value)}
                    labelTextColor={theme.palette.white}
                    colors={(serie) => serie.data.color || theme.palette.secondary1.grey}
                    parentLabelTextColor={theme.palette.customtext.black54}
                    nodeOpacity={1}
                    orientLabel={true}
                    borderColor={theme.palette.secondary1.grey}
                    borderWidth={0}
                    innerPadding={4}
                    outerPadding={8}
                    tooltip={(node) => (
                        <TreeMapPortfolioTooltip
                            {...node}
                            marketValueLabel={tooltipMarketValueLabel}
                            valueTrendLabel={tooltipValueTrendLabel}
                            portfolioShareLabel={tooltipPortfolioShareLabel}
                            formatValue={formatTooltipValue}
                            formatPercentage={formatTooltipPercentage}
                        />
                    )}
                    nodeComponent={TreeMapHtmlNode}
                    onClick={(node: ComputedNode<TreeMapPortfolioDatum>) => {
                        if (node.data.href) {
                            void navigate(node.data.href)
                        }
                    }}
                />
            ) : (
                <EmptyState title={emptyStateTitle} size="enormous" textColor={tokens.ColorTextDefault} />
            )}
        </>
    )
}

const customTheme = (theme: Theme): ComponentProps<typeof ResponsiveTreeMapHtml>['theme'] =>
    withNivoTooltipWrapperTheme({
        labels: {
            text: {
                fontFamily: theme.typography.fontFamily,
                fontSize: theme.typography.bodyTextSmall.fontSize,
            },
        },
    })

const getTrendColor = (trend?: number) => {
    if (trend === undefined || trend === 0) {
        return Colors.status.green
    }
    if (trend > 0.05) {
        return Colors.status.green120
    }
    if (trend < -0.05) {
        return Colors.status.red120
    }
    if (trend < 0) {
        return Colors.status.red
    }
    return Colors.status.green
}
